import { useEffect, useRef, useState } from 'react';
import { DatePicker } from '../../../components/datePicker.tsx';
import { ModalSubmitDialog } from '../../../components/modalSubmitDialog.tsx';
import { OperatorSelect } from '../../../components/operatorSelect.tsx';
import { ManualTask } from '../../../types/sharedTypeImpl.ts';
import { trpc } from '../../../utils/trpc/trpc.ts';


interface Props {
    task?: ManualTask;
    onSuccess: () => void;
    onClose: () => void;
}

export function ManualTaskDialog({
    task,
    onSuccess,
    onClose,
}: Props) {
    const formRef = useRef<HTMLFormElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const [text, setText] = useState(task?.text ?? '');
    const [scheduledAt, setScheduledAt] = useState<Date | null>(task?.scheduledAt ?? null);
    const [createdBy, setCreatedBy] = useState<string>(task?.createdBy ?? '');
    const [editedBy, setEditedBy] = useState<string>('');

    const isNewTask = !task;
    const isEdit = !!task;
    const taskEdits = task?.edits ?? [];

    const upsertTask = trpc.tasks.upsertManualTask.useMutation();


    useEffect(() => {
        inputRef.current?.focus();
    }, []);

    function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
        if (e.key === 'Enter') {
            handleSubmit();
        }
    }

    async function handleSubmit() {
        if (!formRef.current?.checkValidity()) {
            formRef.current!.reportValidity();
            return;
        }
        if (scheduledAt && isNewTask && scheduledAt < new Date()) {
            alert('Scheduled time must be in the future');
            return;
        }

        const newTask: ManualTask = {
            ...task,
            text,
            createdBy,
            ...(scheduledAt && { scheduledAt }),
            isCompleted: false,
            isCanceled: false,
            ...(isEdit && { edits: [...taskEdits, { editedBy }] }),
        };
        try {
            await upsertTask.mutateAsync(newTask);
            onSuccess();
            onClose();
        } catch (e) {
            console.log(e);
            alert(task ? 'Failed to update task' : 'Failed to create task');
        }
    }

    return (
        <ModalSubmitDialog
            frameStyle={{ width: '500px' }}
            isOpen={true}
            isSubmitting={upsertTask.isPending}
            onSubmit={handleSubmit}
            onCancel={onClose}
        >
            <form ref={formRef} >
                <div className='dialog-form__field--with-margins'>
                    <div>Task</div>
                    <input
                        ref={inputRef}
                        style={{ width: '100%' }}
                        type='text'
                        value={text}
                        onChange={(e) => setText(e.target.value)}
                        onKeyDown={handleKeyDown}
                        autoFocus
                        required
                    />
                </div>

                <div className='dialog-form__field--with-margins'>
                    <div style={{ marginRight: '0.4em' }}>Schedule at</div>
                    <DatePicker
                        wrapperClassName='scheduled-at__picker'
                        className='scheduled-at__picker'
                        dateFormat='dd.MM.yyyy HH:mm'
                        showTimeSelect
                        selected={scheduledAt}
                        onChange={(date) => setScheduledAt(date)}
                    />
                </div>

                <div className='dialog-form__field--with-margins'>
                    <div>Created by</div>
                    <OperatorSelect
                        style={{ width: '100%' }}
                        operatorInitials={createdBy}
                        onChange={(value) => setCreatedBy(value)}
                        required
                        disabled={isEdit}
                    />
                </div>

                {isEdit &&
                    <div className='dialog-form__field--with-margins'>
                        <div>Edited by</div>
                        {taskEdits.map((edit, index) =>
                            <OperatorSelect
                                key={index}
                                style={{ width: '100%' }}
                                operatorInitials={edit.editedBy}
                                disabled={true}
                            />
                        )}
                        <OperatorSelect
                            style={{ width: '100%' }}
                            operatorInitials={editedBy}
                            onChange={(value) => setEditedBy(value)}
                            required
                            disabled={!isEdit}
                        />
                    </div>
                }
            </form>
        </ModalSubmitDialog>
    )
}
